import { z } from 'zod';

/** Respuesta de error uniforme de la API: la interfaz traduce `code` con errors.<code>. */
export const ApiErrorSchema = z.object({
  error: z.object({
    code: z.string(),
    params: z.record(z.unknown()).default({}),
  }),
});
export type ApiError = z.infer<typeof ApiErrorSchema>;

/** GET /api/health */
export const HealthResponseSchema = z.object({
  status: z.literal('ok'),
  env: z.string(),
  database: z.object({
    database: z.string(),
    version: z.string(),
    migrations: z.number().int().nonnegative(),
    timezone: z.literal('America/Bogota'),
    server_time: z.string(),
  }),
});
export type HealthResponse = z.infer<typeof HealthResponseSchema>;

/** Códigos de error conocidos. Cada uno tiene traducción es/en en la interfaz. */
export const ERROR_CODES = [
  // infraestructura y API
  'DB_UNAVAILABLE', 'NETWORK_ERROR', 'NOT_FOUND', 'BAD_REQUEST', 'VALIDATION_FAILED', 'INTERNAL_ERROR',
  'AUTH_REQUIRED', 'INVALID_CREDENTIALS', 'FORBIDDEN', 'CSRF_ORIGIN', 'RATE_LIMITED', 'IDEMPOTENCY_KEY_REQUIRED',
  'REQUEST_ID_REQUIRED', 'REQUEST_ID_CONFLICT', 'CANNOT_LOCK_SELF',
  // ubicaciones y configuración
  'LOCATION_NOT_FOUND', 'LOCATION_FORBIDDEN', 'LOCATION_CODE_EXISTS', 'LOCATION_HAS_STOCK', 'LOCATION_REQUIRED_FOR_ROLE',
  'ROLE_NOT_FOUND', 'EMAIL_EXISTS', 'USERNAME_EXISTS', 'INVALID_USERNAME', 'PASSWORD_CHANGE_REQUIRED',
  'INVALID_AMOUNT', 'RECIPIENT_REQUIRED', 'CASH_INSUFFICIENT', 'LOCATION_REQUIRED', 'NOT_AUTHORIZED_YET', 'OVER_AUTHORIZED', 'PAYMENT_METHOD_EXISTS',
  // catálogo
  'PRODUCT_REF_EXISTS', 'PRODUCT_BARCODE_EXISTS', 'PRODUCT_NAME_EXISTS', 'PRODUCT_NAME_SIMILAR',
  'PRODUCT_REF_PROTECTED', 'PRODUCT_BARCODE_PROTECTED', 'PRODUCT_HAS_STOCK', 'PRODUCT_HAS_HISTORY', 'VARIANTS_REQUIRED',
  'VARIANT_GRANULARITY_MISMATCH', 'VARIANT_NOT_FOUND', 'CURVE_CODE_EXISTS', 'CURVE_LINES_REQUIRED',
  'EMPTY_IMPORT', 'IMPORT_HAS_ERRORS',
  // inventario
  'STOCK_INSUFFICIENT', 'INVALID_QUANTITY', 'INVALID_COST', 'INVALID_MOVEMENT_TYPE', 'EMPTY_OPERATION', 'APPEND_ONLY',
  // operaciones
  'LOCATION_NOT_DISTRIBUTOR', 'INVALID_LOCATION_TYPE', 'INVALID_CURVE_FACTOR', 'CURVE_FRACTION', 'LOCATION_CANNOT_DISPATCH',
  'INVALID_DESTINATION', 'RECEIPT_LINES_MISMATCH', 'RECEIPT_SUM_MISMATCH', 'INVALID_STATUS', 'INVALID_CHANNEL',
  'PAYMENTS_REQUIRED', 'PAYMENT_METHOD_NOT_FOUND', 'PAYMENTS_DO_NOT_MATCH_TOTAL', 'CREDIT_CUSTOMER_REQUIRED', 'REASON_REQUIRED',
  'RETURN_EXCEEDS_SOLD', 'FULL_COUNT_CONFIRMATION_REQUIRED', 'ALREADY_REVERSED', 'NOT_REVERSIBLE', 'SHIPMENT_ALREADY_RECEIVED',
  'SALE_HAS_RETURNS', 'CREDIT_HAS_PAYMENTS', 'NO_SURPLUS', 'PAYMENT_OVER_BALANCE', 'INVALID_PAYMENT_METHOD',
  // importación de inventario y despacho masivo (motivos por fila)
  'UNKNOWN_REF', 'UNKNOWN_VARIANT', 'GRANULARITY_MISMATCH', 'INVALID_QTY', 'DUPLICATE_ROW', 'VARIANT_REQUIRED',
] as const;
export type ErrorCode = (typeof ERROR_CODES)[number];

export const TIME_ZONE = 'America/Bogota';
export const CURRENCY = 'COP';
export const LANGUAGES = ['es', 'en'] as const;
export const LanguageSchema = z.enum(LANGUAGES);
export type Language = z.infer<typeof LanguageSchema>;

export const IdSchema = z.coerce.number().int().positive();
export const IdParamsSchema = z.object({ id: IdSchema });
export const IdResultSchema = z.object({ id: z.number().int() });
export const UuidSchema = z.string().uuid();
export const MoneySchema = z.coerce.number().min(0).max(99_999_999_999);
